import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Flex,
  SimpleGrid,
  Text,
  RadioGroup,
  Field,
} from '@chakra-ui/react';
import { DrawerComponent } from '../../ui/drawerComponent';
import {
  Button as ButtonComponent,
  InputField,
  SectionCard,
  SelectInput,
  Textarea,
  ToggleButton,
} from '@themes/aegix';
import {
  useCreateDrillSchedule,
  useUpdateDrillSchedule,
} from '../../../hooks/queries';
import { useSites, useAlertTypes } from '../../../hooks/queries';
import {
  DRILL_REMINDER_MILESTONES,
  patternFromRecurrenceRule,
  recurrenceRuleFromPattern,
  type DrillReminderMilestone,
  type DrillSchedule,
  type DrillSchedulePayload,
  type DrillScheduleStatus,
  type RecurrencePattern,
} from '../../../services';
import {
  ProtocolGroupSelector,
  type GroupSelection,
} from './ProtocolGroupSelector';
import { toaster } from '../../ui/toaster';

interface DrillScheduleDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  schedule: DrillSchedule | null;
  defaultSiteId?: number | null;
}

const ENGINE_OWNED_STATUSES: DrillScheduleStatus[] = ['started', 'pending_approval'];

/** Split an ISO datetime into local `YYYY-MM-DD` + `HH:MM` form values. */
function splitLocal(iso: string): { date: string; time: string } {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { date: '', time: '09:00' };
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
}

/** Compose local date+time form values into an ISO datetime string. */
function composeIso(date: string, time: string): string {
  return new Date(`${date}T${time || '00:00'}:00`).toISOString();
}

export function DrillScheduleDrawer({
  isOpen,
  onClose,
  schedule,
  defaultSiteId,
}: DrillScheduleDrawerProps) {
  const { t } = useTranslation();
  const isEditing = !!schedule;
  const createMutation = useCreateDrillSchedule();
  const updateMutation = useUpdateDrillSchedule();

  const recurrenceItems: { value: RecurrencePattern; label: string }[] = useMemo(
    () => [
      { value: 'once', label: t('sites.recurrenceOnce', 'One-time') },
      { value: 'weekly', label: t('sites.recurrenceWeekly', 'Weekly') },
      { value: 'monthly', label: t('sites.recurrenceMonthly', 'Monthly') },
      { value: 'quarterly', label: t('sites.recurrenceQuarterly', 'Quarterly') },
      { value: 'annually', label: t('sites.recurrenceAnnually', 'Annually') },
    ],
    [t],
  );

  const statusItems: { value: DrillScheduleStatus; label: string }[] = useMemo(
    () => [
      { value: 'scheduled', label: t('sites.statusScheduled', 'Scheduled') },
      { value: 'completed', label: t('sites.statusCompleted', 'Completed') },
      { value: 'cancelled', label: t('sites.statusCancelled', 'Cancelled') },
    ],
    [t],
  );

  const milestoneLabels: Record<DrillReminderMilestone, string> = useMemo(
    () => ({
      prior_month: t('sites.remindersMilestones.priorMonth', 'Prior month'),
      prior_week: t('sites.remindersMilestones.priorWeek', 'Prior week'),
      prior_day: t('sites.remindersMilestones.priorDay', 'Prior day'),
      same_day: t('sites.remindersMilestones.sameDay', 'Same day'),
    }),
    [t],
  );

  const { data: sitesData } = useSites({ page_size: 100 });
  const [selectedSiteId, setSelectedSiteId] = useState<number>(0);

  const { data: alertTypesData } = useAlertTypes({
    school: selectedSiteId || undefined,
    page_size: 200,
    deleted: false,
  });

  const siteOptions = useMemo(
    () =>
      sitesData?.results?.map((s) => ({
        label: s.name,
        value: String(s.id),
      })) ?? [],
    [sitesData],
  );

  const protocolOptions = useMemo(
    () =>
      alertTypesData?.results?.map((at) => ({
        label: at.name,
        value: String(at.id),
      })) ?? [],
    [alertTypesData],
  );

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [alertType, setAlertType] = useState<number | null>(null);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('09:00');
  const [recurrence, setRecurrence] = useState<RecurrencePattern>('once');
  const [status, setStatus] = useState<DrillScheduleStatus>('scheduled');
  const [isSilent, setIsSilent] = useState(false);
  // Automation config
  const [notifyGroups, setNotifyGroups] = useState<GroupSelection[]>([]);
  const [reminders, setReminders] = useState<DrillReminderMilestone[]>(['prior_week']);
  const [requiresApproval, setRequiresApproval] = useState(false);

  const neverRemind = reminders.length === 0;
  const statusLocked = isEditing && ENGINE_OWNED_STATUSES.includes(schedule?.status ?? '');

  // Reset form on open/change
  useEffect(() => {
    if (schedule) {
      const { date, time } = splitLocal(schedule.scheduled_date);
      setName(schedule.name);
      setDescription(schedule.description || '');
      setSelectedSiteId(schedule.school);
      setAlertType(schedule.alert_type);
      setScheduledDate(date);
      setScheduledTime(time);
      setRecurrence(patternFromRecurrenceRule(schedule.recurrence_rule));
      setStatus(schedule.status);
      setIsSilent(schedule.is_silent ?? false);
      setNotifyGroups(
        (schedule.notify_thread_ids ?? []).map((id) => ({
          id,
          // The list payload carries ids only; the selector's member-peek
          // resolves names lazily. Show the id until the user re-picks.
          name: t('sites.groupWithId', 'Group {{id}}', { id }),
        })),
      );
      setReminders(schedule.reminders ?? ['prior_week']);
      setRequiresApproval(schedule.requires_approval ?? false);
    } else {
      setName('');
      setDescription('');
      setSelectedSiteId(defaultSiteId ?? 0);
      setAlertType(null);
      setScheduledDate('');
      setScheduledTime('09:00');
      setRecurrence('once');
      setStatus('scheduled');
      setIsSilent(false);
      setNotifyGroups([]);
      setReminders(['prior_week']);
      setRequiresApproval(false);
    }
  }, [schedule, isOpen, defaultSiteId]);

  const toggleMilestone = (milestone: DrillReminderMilestone) => {
    setReminders((prev) =>
      prev.includes(milestone)
        ? prev.filter((m) => m !== milestone)
        : [...prev, milestone],
    );
  };

  const handleNeverToggle = (checked: boolean) => {
    // "Never" is mutually exclusive with the milestones: checking it clears
    // them; checking any milestone (above) implicitly unchecks Never.
    if (checked) setReminders([]);
    else setReminders(['prior_week']);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toaster.create({
        title: t('sites.titleIsRequired', 'Title is required'),
        type: 'error',
        duration: 3000,
      });
      return;
    }
    if (!selectedSiteId) {
      toaster.create({
        title: t('sites.siteIsRequired', 'Site is required'),
        type: 'error',
        duration: 3000,
      });
      return;
    }
    if (!alertType) {
      toaster.create({
        title: t('sites.protocolIsRequired', 'Protocol is required'),
        type: 'error',
        duration: 3000,
      });
      return;
    }
    if (!scheduledDate) {
      toaster.create({
        title: t('sites.dateIsRequired', 'Date is required'),
        type: 'error',
        duration: 3000,
      });
      return;
    }

    const payload: DrillSchedulePayload = {
      name: name.trim(),
      description,
      school: selectedSiteId,
      alert_type: alertType,
      scheduled_date: composeIso(scheduledDate, scheduledTime),
      // 'custom' preserves the schedule's original hand-authored RRULE.
      recurrence_rule:
        recurrence === 'custom'
          ? (schedule?.recurrence_rule ?? null)
          : recurrenceRuleFromPattern(recurrence),
      is_silent: isSilent,
      notify_thread_ids: notifyGroups.map((g) => g.id),
      reminders,
      requires_approval: requiresApproval,
    };
    if (isEditing && !statusLocked) {
      payload.status = status;
    }

    try {
      if (isEditing && schedule) {
        await updateMutation.mutateAsync({ id: schedule.id, data: payload });
      } else {
        await createMutation.mutateAsync(payload);
      }

      toaster.create({
        title: isEditing ? t('sites.drillScheduleUpdated', 'Drill schedule updated') : t('sites.drillScheduleCreated', 'Drill schedule created'),
        type: 'success',
        duration: 3000,
      });
      onClose();
    } catch (err) {
      toaster.create({
        title: t('sites.error', 'Error'),
        description:
          err instanceof Error ? err.message : 'Something went wrong',
        type: 'error',
        duration: 4000,
      });
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <DrawerComponent
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? t('sites.editDrillTitle', 'Edit Drill — {{name}}', { name: schedule?.name }) : t('sites.scheduleDrillTitle', 'Schedule Drill')}
      size="md"
      footer={
        <SimpleGrid columns={2} gap={4} width="100%">
          <ButtonComponent visual="secondary" onClick={onClose} data-e2e="cancel-button">
            {t('common.cancel', 'Cancel')}
          </ButtonComponent>
          <ButtonComponent
            visual="primary"
            onClick={handleSave}
            disabled={isSaving}
            data-e2e="confirm-schedule-button"
          >
            {isSaving ? t('common.saving', 'Saving...') : isEditing ? t('common.saveChanges', 'Save Changes') : t('sites.schedule', 'Schedule')}
          </ButtonComponent>
        </SimpleGrid>
      }
    >
      <Flex direction="column" gap={4}>
        <SectionCard title={t('sites.drillDetails', 'Drill Details')}>
          <Flex direction="column" gap={4}>
            <Field.Root>
              <Field.Label htmlFor="drill-title">{t('common.title', 'Title')}</Field.Label>
              <InputField
                id="drill-title"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('sites.drillTitlePlaceholder', 'e.g. Fire Drill Q1')}
                data-e2e="drill-title-input"
              />
            </Field.Root>

            <SelectInput
              id="drill-site"
              name="drill-site"
              label={t('nav.sites', 'Site')}
              placeholder={t('sites.selectSitePlaceholder', 'Select a site…')}
              value={selectedSiteId ? String(selectedSiteId) : ''}
              onChange={(v) => setSelectedSiteId(Number(v))}
              data-e2e="drill-site-select"
              option={siteOptions}
            />

            <SelectInput
              id="drill-protocol"
              name="drill-protocol"
              label={t('sites.protocol', 'Protocol')}
              placeholder={t('sites.selectProtocolPlaceholder', 'Select a protocol…')}
              value={alertType ? String(alertType) : ''}
              onChange={(v) => setAlertType(v ? Number(v) : null)}
              data-e2e="drill-protocol-select"
              option={protocolOptions}
            />

            <SimpleGrid columns={2} gap={4}>
              <Field.Root>
                <Field.Label htmlFor="drill-date">{t('sites.date', 'Date')}</Field.Label>
                <InputField
                  id="drill-date"
                  type="date"
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
                  data-e2e="drill-date-input"
                />
              </Field.Root>
              <Field.Root>
                <Field.Label htmlFor="drill-time">{t('sites.time', 'Time')}</Field.Label>
                <InputField
                  id="drill-time"
                  type="time"
                  value={scheduledTime}
                  onChange={(e) => setScheduledTime(e.target.value)}
                  data-e2e="drill-time-input"
                />
              </Field.Root>
            </SimpleGrid>
          </Flex>
        </SectionCard>

        <SectionCard title={t('sites.notifyGroups', 'Notify Groups')}>
          <Flex direction="column" gap={2}>
            <Text fontSize="xs" color="text.secondary">
              {t('sites.notifyGroupsDescription', 'All users of the selected chat group(s) are notified of reminders and drill start via chat, push and email — with a text message if push delivery fails.')}
            </Text>
            <ProtocolGroupSelector
              schoolId={selectedSiteId || null}
              selectedGroups={notifyGroups}
              onGroupsChange={setNotifyGroups}
              emptyStateText={t('sites.noGroupsSelectedNoNotifications', 'No groups selected — no drill notifications will be sent.')}
            />
          </Flex>
        </SectionCard>

        <SectionCard title={t('sites.reminders', 'Reminders')}>
          <Flex direction="column" gap={3}>
            <Text fontSize="xs" color="text.secondary">
              {t('sites.remindersDescription', 'Each selected milestone sends one reminder to the notify group(s) ahead of the drill.')}
            </Text>
            {DRILL_REMINDER_MILESTONES.map((m) => (
              <ToggleButton
                key={m.value}
                title={milestoneLabels[m.value] ?? m.label}
                checked={reminders.includes(m.value)}
                onCheckedChange={() => toggleMilestone(m.value)}
                e2eTitle={`reminder-${m.value.replace('_', '-')}-toggle`}
              />
            ))}
            <ToggleButton
              title={t('common.never', 'Never')}
              checked={neverRemind}
              onCheckedChange={(details: { checked: boolean }) =>
                handleNeverToggle(details.checked)
              }
              e2eTitle="reminder-never-toggle"
            />
          </Flex>
        </SectionCard>

        <SectionCard title={t('sites.scheduleSection', 'Schedule')}>
          <Flex direction="column" gap={4}>
            <SelectInput
              id="drill-recurrence"
              name="drill-recurrence"
              label={t('sites.recurrence', 'Recurrence')}
              value={recurrence}
              onChange={(v) => setRecurrence(v as RecurrencePattern)}
              data-e2e="drill-recurrence-select"
              option={(recurrence === 'custom'
                ? [...recurrenceItems, { value: 'custom' as const, label: t('common.custom', 'Custom') }]
                : recurrenceItems
              ).map((opt) => ({ label: opt.label, value: opt.value }))}
            />

            {isEditing && statusLocked && (
              <Field.Root>
                <Field.Label>{t('common.status', 'Status')}</Field.Label>
                <Text fontSize="sm" color="text.secondary" data-e2e="drill-status-locked">
                  {schedule?.status === 'pending_approval'
                    ? t('sites.pendingApprovalDescription', 'Pending approval — awaiting an approver decision.')
                    : t('sites.startedDescription', 'Started — managed by the drill engine.')}
                </Text>
              </Field.Root>
            )}
            {isEditing && !statusLocked && (
              <Field.Root as="fieldset">
                <Field.Label as="legend">{t('common.status', 'Status')}</Field.Label>
                <RadioGroup.Root
                  value={status}
                  onValueChange={(e) => setStatus(e.value as DrillScheduleStatus)}
                  display="flex"
                  gap={2}
                  data-e2e="drill-status-select"
                >
                  {statusItems.map((opt) => (
                    <RadioGroup.Item key={opt.value} value={opt.value}>
                      <RadioGroup.ItemHiddenInput />
                      <RadioGroup.ItemControl />
                      <RadioGroup.ItemText fontSize="sm">
                        {opt.label}
                      </RadioGroup.ItemText>
                    </RadioGroup.Item>
                  ))}
                </RadioGroup.Root>
              </Field.Root>
            )}

            <Field.Root>
              <ToggleButton
                title={t('sites.requireApproval', 'Require Approval')}
                checked={requiresApproval}
                onCheckedChange={(details: { checked: boolean }) =>
                  setRequiresApproval(details.checked)
                }
                e2eTitle="requires-approval-toggle"
              />
              <Text fontSize="xs" color="text.secondary" mt={1}>
                {t('sites.requireApprovalHelper', 'Drill start must be approved, like alert approvals')}
              </Text>
            </Field.Root>

            <Field.Root>
              <ToggleButton
                title={t('sites.silentDrill', 'Silent Drill')}
                checked={isSilent}
                onCheckedChange={(details: { checked: boolean }) =>
                  setIsSilent(details.checked)
                }
                e2eTitle="silent-drill-toggle"
              />
              <Text fontSize="xs" color="text.secondary" mt={1}>
                {t('sites.silentDrillHelper', 'No audible alerts during drill')}
              </Text>
            </Field.Root>
          </Flex>
        </SectionCard>

        <SectionCard title={t('sites.notes', 'Notes')}>
          <Field.Root>
            <Field.Label htmlFor="drill-description">{t('common.description', 'Description')}</Field.Label>
            <Textarea
              id="drill-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('sites.drillDescriptionPlaceholder', 'Drill description or objectives…')}
              rows={3}
              data-e2e="drill-description-input"
            />
          </Field.Root>
        </SectionCard>
      </Flex>
    </DrawerComponent>
  );
}
